import _ from 'lodash';
import React from 'react';
import styled from 'styled-components';

import jersey from '../../../assets/jersey-wrapper.png';
import { ITeam } from './check-in.interface';
import { Checkbox } from '@material-ui/core';
import { getPlayerName } from '../../../lib/utils/player-name.service';
import { roundTo } from '../../../lib/helpers/number.helpers';
import { getContrastTextColor, resolvePaletteColor, WCAG_AA_LARGE_TEXT_MIN_CONTRAST } from '../../../lib/helpers/color.helpers';
import { PaletteColor } from '../../../lib/types';

// Jersey colours offered at check-in, in picker order.
export const JERSEY_COLORS: PaletteColor[] = [
  PaletteColor.RED, PaletteColor.ORANGE, PaletteColor.YELLOW, PaletteColor.GREEN, PaletteColor.BLUE,
  PaletteColor.PURPLE, PaletteColor.PINK, PaletteColor.BLACK, PaletteColor.WHITE, PaletteColor.GREY,
];

// Not in JERSEY_COLORS, so an unassigned jersey never looks like a pickable one.
const UNASSIGNED_JERSEY_COLOR = PaletteColor.SILVER;

// A white jersey is drawn light grey so it still shows on a white page.
export const getJerseyFillColor = (color?: string): string => {
  if (!color) {
    return UNASSIGNED_JERSEY_COLOR.hex;
  }
  return _.toLower(color) === PaletteColor.WHITE.name ? PaletteColor.LIGHT_GREY.hex : resolvePaletteColor(color);
};

// The jersey number is 16px bold (WCAG large text), so 3:1 keeps white wherever it's legible.
export const getJerseyTextColor = (color?: string): string =>
  getContrastTextColor(getJerseyFillColor(color), { minContrast: WCAG_AA_LARGE_TEXT_MIN_CONTRAST });

// The jersey a player last wore in this assessment; check-in offers it only when jersey retention is on.
export const carriedJersey = (playerAssessment: IPlayerAssessment): IJersey | null => (
  playerAssessment.jerseyColour && _.isNumber(playerAssessment.jerseyNumber)
    ? { color: playerAssessment.jerseyColour.toLowerCase(), number: playerAssessment.jerseyNumber }
    : null
);

export const buildColors = (teamPlayers: IPlayerAssessment[], jerseys: { [key: string]: IJersey }, checkedInPlayerIds: string[], retainJerseyAssignments: boolean) => {
  const colors: { [key: string]: string[] } = {};

  teamPlayers.forEach(playerAssessment => {
    const jersey = retainJerseyAssignments ? { color: playerAssessment.jerseyColour, number: playerAssessment.jerseyNumber } : jerseys[playerAssessment._id];
    const isCheckedIn = _.includes(checkedInPlayerIds, playerAssessment._id);

    if ((isCheckedIn || retainJerseyAssignments) && jersey && jersey.color) {
      colors[playerAssessment.position.name] = colors[playerAssessment.position.name] || [];
      if (!_.includes(colors[playerAssessment.position.name], jersey.color)) {
        colors[playerAssessment.position.name].push(jersey.color);
      }
    }
  });

  return colors;
};

export function getPlayersByPosition(playerAssessments: IPlayerAssessment[]): Record<string, IPlayerAssessment[]> {
  return playerAssessments.reduce((result: { [key: string]: IPlayerAssessment[] }, playerAssessment) => {
    const positionId = playerAssessment.position._id;

    result[positionId] = result[positionId] || [];
    result[positionId].push(playerAssessment);

    return result;
  }, {});
}

// Returns a hash where the key is the position ID, and the value is an array of PlayerAssesments, sorted by rank.
export function getPlayersByPositionAndRank(positionPlayers: { [key: string]: IPlayerAssessment[] }): { [key: string]: IPlayerAssessment[] } {
  const result: { [key: string]: IPlayerAssessment[] } = {};

  Object.keys(positionPlayers).forEach((position: string) => {
    // Sort by rank.
    result[position] = positionPlayers[position].sort((a, b) => {
      return (b.adjustedScore && b.adjustedScore !== "-" ? Number(b.adjustedScore) : 0) - (a.adjustedScore && a.adjustedScore !== "-" ? Number(a.adjustedScore) : 0);
    });
  });

  return result;
}

// Helper function to calculate team average rank
const calculateTeamAverageRank = (team: IPlayerAssessment[], allPlayers: IPlayerAssessment[]) => {
  if (team.length === 0) return Infinity; // Empty teams get lowest priority

  // Sort all players by adjusted score for overall ranking
  const sortedAllPlayers = [...allPlayers].sort((a, b) => {
    return (b.adjustedScore && b.adjustedScore !== "-" ? Number(b.adjustedScore) : 0) -
           (a.adjustedScore && a.adjustedScore !== "-" ? Number(a.adjustedScore) : 0);
  });

  let totalRank = 0;
  team.forEach(player => {
    const overallRank = sortedAllPlayers.findIndex(p => p._id === player._id) + 1;
    totalRank += overallRank;
  });

  return totalRank / team.length;
};

// Intelligent snake-draft that ensures even position distribution while balancing team strength
export const buildTeamPlayersSnakeDraft = (playersByPositionAndRank: { [key: string]: IPlayerAssessment[] }, numTeams: number) => {
  console.debug('🚨 ENTRY: buildTeamPlayersSnakeDraft function called!');
  console.debug('🏈 Starting Position-Balanced Snake Draft with', numTeams, 'teams');
  console.debug('📋 Position groups:', Object.keys(playersByPositionAndRank));

  // Add validation logging
  if (!playersByPositionAndRank || Object.keys(playersByPositionAndRank).length === 0) {
    console.error('❌ ERROR: No players provided to buildTeamPlayersSnakeDraft');
    return [];
  }

  if (numTeams < 1) {
    console.error('❌ ERROR: Invalid number of teams:', numTeams);
    return [];
  }

  const teams: IPlayerAssessment[][] = [];

  // Initialize teams array
  for (let i = 0; i < numTeams; i++) {
    teams[i] = [];
  }

  // Get all players for rank calculations
  const allPlayers = _.flatten(_.values(playersByPositionAndRank));
  console.debug('👥 Total players:', allPlayers.length);

  // Sort positions by number of players (most to least) for better distribution
  const sortedPositions = _.sortBy(Object.entries(playersByPositionAndRank), ([_, players]) => -players.length);

  console.debug('📊 Position order (by player count):', sortedPositions.map(([pos, players]) => `${pos}: ${players.length} players`));

  // Process each position group to ensure even distribution
  sortedPositions.forEach(([positionName, positionPlayers]) => {
    console.debug(`\n🎯 Processing position: ${positionName} (${positionPlayers.length} players)`);
    console.debug('🏆 Players by rank:', positionPlayers.map(p => `${p.player.firstName} ${p.player.lastName} (${p.adjustedScore})`));

    // Calculate how many players each team should get from this position
    const playersPerTeam = Math.floor(positionPlayers.length / numTeams);
    const extraPlayers = positionPlayers.length % numTeams;

    console.debug(`📊 Distribution plan: ${playersPerTeam} per team, ${extraPlayers} extra players`);

    // Show current team balance before starting this position
    if (teams.some(team => team.length > 0)) {
      const preBalanceRanks = teams.map((team, index) => {
        const avgRank = calculateTeamAverageRank(team, allPlayers);
        return `Team ${index + 1}: ${avgRank.toFixed(1)} (${team.length} players)`;
      });
      console.debug('⚖️ Current team balance:', preBalanceRanks.join(', '));
    }

    // Distribute players for this position round by round
    let playerIndex = 0;

    // Determine initial draft order for this position based on current team balance
    const teamStats = teams.map((team, index) => ({
      index,
      playerCount: team.length,
      avgRank: calculateTeamAverageRank(team, allPlayers)
    }));

    // Sort teams to determine initial draft order:
    // 1. Teams with fewer players go first
    // 2. Among teams with same player count, weakest (highest avg rank) goes first
    teamStats.sort((a, b) => {
      if (a.playerCount !== b.playerCount) {
        return a.playerCount - b.playerCount; // Fewer players first
      }
      return b.avgRank - a.avgRank; // Higher avg rank (weaker team) first
    });

    // Create the base draft order for this position
    const baseDraftOrder = teamStats.map(t => t.index);
    console.debug(`📋 Base draft order for ${positionName}: ${baseDraftOrder.map(i => `Team ${i + 1}`).join(' → ')}`);
    console.debug(`📊 Initial team stats: ${teamStats.map(t => `T${t.index + 1}(${t.playerCount} players, ${t.avgRank.toFixed(1)} avg)`).join(', ')}`);

    // Process each round of distribution for this position
    for (let round = 0; round < playersPerTeam; round++) {
      console.debug(`\n📍 Round ${round + 1} for ${positionName}`);

      // Apply snake pattern: use base order on even rounds, reverse on odd rounds
      const isSnakeReverse = round % 2 === 1;
      const roundDraftOrder = isSnakeReverse ? [...baseDraftOrder].reverse() : [...baseDraftOrder];

      console.debug(`  📋 Draft order: ${roundDraftOrder.map(i => `Team ${i + 1}`).join(' → ')} ${isSnakeReverse ? '(snake reversed)' : ''}`);

      // Distribute players in the determined order
      for (let pick = 0; pick < numTeams && playerIndex < positionPlayers.length; pick++) {
        const teamIndex = roundDraftOrder[pick];
        const player = positionPlayers[playerIndex];
        teams[teamIndex].push(player);
        console.debug(`    ➤ Pick ${pick + 1}: ${player.player.firstName} ${player.player.lastName} → Team ${teamIndex + 1}`);
        playerIndex++;
      }
    }

    // Handle extra players (if position count doesn't divide evenly)
    if (extraPlayers > 0) {
      const extraRound = playersPerTeam; // This is the next round number
      console.debug(`\n📍 Round ${extraRound + 1} for ${positionName} (partial - ${extraPlayers} players)`);

      // Check if this is the first position being distributed
      const isFirstPosition = teams.every(team =>
        team.every(player => player.position._id === positionName)
      );

      let extraDraftOrder: number[];

      if (isFirstPosition) {
        // For the first position, continue the snake pattern
        const isSnakeReverse = extraRound % 2 === 1;
        extraDraftOrder = isSnakeReverse ? [...baseDraftOrder].reverse() : [...baseDraftOrder];
        extraDraftOrder = extraDraftOrder.slice(0, extraPlayers);
        console.debug(`  📋 Draft order: ${extraDraftOrder.map(i => `Team ${i + 1}`).join(' → ')} ${isSnakeReverse ? '(snake reversed - first position)' : '(first position)'}`);
      } else {
        // For subsequent positions, re-evaluate based on current team balance
        const currentTeamStats = teams.map((team, index) => ({
          index,
          playerCount: team.length,
          avgRank: calculateTeamAverageRank(team, allPlayers)
        }));

        // Sort to prioritize teams with fewer players, then by weakest team
        currentTeamStats.sort((a, b) => {
          if (a.playerCount !== b.playerCount) {
            return a.playerCount - b.playerCount; // Fewer players first
          }
          return b.avgRank - a.avgRank; // Higher avg rank (weaker team) as tiebreaker
        });

        extraDraftOrder = currentTeamStats.slice(0, extraPlayers).map(t => t.index);
        console.debug(`  📋 Re-evaluated draft order: ${extraDraftOrder.map(i => `Team ${i + 1}`).join(' → ')} (balanced distribution)`);
        console.debug(`  📊 Current balance: ${currentTeamStats.slice(0, extraPlayers).map(t => `T${t.index + 1}(${t.playerCount} players, ${t.avgRank.toFixed(1)} avg)`).join(', ')}`);
      }

      // Distribute extra players in the determined order
      for (let extra = 0; extra < extraPlayers && playerIndex < positionPlayers.length; extra++) {
        const teamIndex = extraDraftOrder[extra];
        const player = positionPlayers[playerIndex];
        teams[teamIndex].push(player);
        console.debug(`    ➤ Pick ${extra + 1}: ${player.player.firstName} ${player.player.lastName} → Team ${teamIndex + 1}`);
        playerIndex++;
      }
    }

    // Show final balance after this position
    const postBalanceRanks = teams.map((team, index) => {
      const avgRank = calculateTeamAverageRank(team, allPlayers);
      const positionCount = team.filter(p => p.position._id === positionName).length;
      const positionDisplayName = positionPlayers[0]?.position?.name || positionName;
      return `Team ${index + 1}: ${avgRank.toFixed(1)} (${team.length} total, ${positionCount} ${positionDisplayName}s)`;
    });
    console.debug(`✅ Balance after ${positionName}:`, postBalanceRanks.join(', '));
  });

  console.debug('🏁 Final team composition:');
  teams.forEach((team, index) => {
    const avgRank = calculateTeamAverageRank(team, allPlayers);
    const positionCounts: { [key: string]: number } = {};
    team.forEach(player => {
      positionCounts[player.position.name] = (positionCounts[player.position.name] || 0) + 1;
    });
    console.debug(`Team ${index + 1} (avg rank: ${avgRank.toFixed(1)}, ${team.length} players):`,
      Object.entries(positionCounts).map(([pos, count]) => `${count} ${pos}${count > 1 ? 's' : ''}`).join(', ')
    );
  });

  return teams;
};

export const buildTeamPlayers = (playerAssessments: IPlayerAssessment[], numTeams: number, useSnakeDraft: boolean = true) => {
  console.log('🔧 buildTeamPlayers called:', {
    playerCount: playerAssessments.length,
    numTeams,
    numTeamsType: typeof numTeams,
    useSnakeDraft,
    playerNames: playerAssessments.map(p => `${p.player.firstName} ${p.player.lastName}`)
  });

  const teams: IPlayerAssessment[][] = [];

  const playersByPosition = getPlayersByPosition(playerAssessments);
  const playersByPositionAndRank = getPlayersByPositionAndRank(playersByPosition);

  console.log('📊 Players by position and rank:', Object.keys(playersByPositionAndRank).map(pos =>
    `${pos}: ${playersByPositionAndRank[pos].length} players`
  ));

  if (useSnakeDraft) {
    console.log('✅ Using snake-draft - calling buildTeamPlayersSnakeDraft');
    return buildTeamPlayersSnakeDraft(playersByPositionAndRank, numTeams);
  }

  console.log('⚠️ Using round-robin (old method)');
  // Original round-robin logic
  let currTeam = 0;
  _.each(playersByPositionAndRank, (positionPlayers) => {
    _.each(positionPlayers, (playerAssessment) => {
      teams[currTeam] = teams[currTeam] || [];
      teams[currTeam].push(playerAssessment);

      currTeam += 1;
      currTeam %= numTeams;
    });
  });

  return teams;
};

export const rebuildTeamPlayers = (teams: string[][], playerAssessments: IPlayerAssessment[], useSnakeDraft: boolean = true) => {
  console.log('🔄 rebuildTeamPlayers called:', {
    savedTeamsCount: teams.length,
    playerCount: playerAssessments.length,
    useSnakeDraft,
    savedTeamSizes: teams.map(team => team.length)
  });

  const result: IPlayerAssessment[][] = [];
  const tracked: { [key: string]: boolean } = {};

  teams.forEach((team, index) => {
    result.push([]);

    team.forEach(playerAssessmentId => {
      const playerAssessment = _.find(playerAssessments, playerAssessments => playerAssessments._id === playerAssessmentId);

      if (playerAssessment) {
        tracked[playerAssessment._id] = true;
        result[index].push(playerAssessment);
      }
    });
  });

  // Get untracked players and assign them using the same logic as buildTeamPlayers
  const untrackedPlayers = playerAssessments.filter(p => !tracked[p._id]);
  if (untrackedPlayers.length === 0) {
    return result;
  }

  const playersByPosition = getPlayersByPosition(untrackedPlayers);
  const playersByPositionAndRank = getPlayersByPositionAndRank(playersByPosition);

  if (useSnakeDraft) {
    // Use intelligent balancing for new players
    const allPlayersForRanking = [..._.flatten(result), ...untrackedPlayers];

    _.each(playersByPositionAndRank, (positionPlayers) => {
      positionPlayers.forEach(player => {
        // Calculate current average ranks for all teams
        const teamAverageRanks = result.map(team => calculateTeamAverageRank(team, allPlayersForRanking));

        // Find the team with the highest average rank (weakest team)
        let weakestTeamIndex = 0;
        let highestAverageRank = teamAverageRanks[0];

        for (let i = 1; i < teamAverageRanks.length; i++) {
          if (teamAverageRanks[i] > highestAverageRank) {
            highestAverageRank = teamAverageRanks[i];
            weakestTeamIndex = i;
          }
        }

        // Assign the player to the weakest team
        result[weakestTeamIndex].push(player);
      });
    });
  } else {
    // Original round-robin logic for untracked players
    let currTeam = 0;
    _.each(playersByPositionAndRank, (rankedPlayers) => {
      _.each(rankedPlayers, (playerAssessment) => {
        if (!tracked[playerAssessment._id] && result[currTeam]) {
          result[currTeam].push(playerAssessment);
        }

        currTeam = (currTeam + 1) % teams.length;
      });
    });
  }

  return result;
};

export const exportTeams = (teams: ITeam[]) => {
  const result: string[][] = [];

  _.each(teams, (team, index) => {
    result.push([]);

    _.each(team.playersByPosition, (players) => {
      _.each(players, player => {
        result[index].push(player._id);
      });
    });
  });

  return result;
}

export const CHECK_IN_SORT_TYPES = {
  JERSEY: 'JERSEY',
  LAST_NAME: 'LAST_NAME',
};

export const sortByPosition = (playerAssessments: IPlayerAssessment[], sortType: string, jerseys: { [key: string]: IJersey }, checkedInPlayerIds: string[] = [], retainJerseyAssignments: boolean) => {
  const playersByPosition: { [key: string]: IPlayerAssessment[] } = {};

  playerAssessments.forEach(playerAssessment => {
    const positionName = playerAssessment.position.name;

    playersByPosition[positionName] = playersByPosition[positionName] || [];
    playersByPosition[positionName].push(playerAssessment);
  });

  const positionNames = Object.keys(playersByPosition);
  positionNames.forEach(positionName => {
    if (sortType === CHECK_IN_SORT_TYPES.JERSEY) {
      let playersWithJersey: { [key: string]: IPlayerAssessment[] } = {};
      let playersWithoutJersey: IPlayerAssessment[] = [];

      _.each(playersByPosition[positionName], playerAssessment => {
        if (retainJerseyAssignments && _.isNumber(playerAssessment.jerseyNumber) && playerAssessment.jerseyColour) {
          playersWithJersey[playerAssessment.jerseyColour.toLowerCase()] = playersWithJersey[playerAssessment.jerseyColour?.toLowerCase()] || [];
          playersWithJersey[playerAssessment.jerseyColour.toLowerCase()].push(playerAssessment);
        } else if (!retainJerseyAssignments && jerseys?.[playerAssessment._id] && _.includes(checkedInPlayerIds || [], playerAssessment._id)) {
          playersWithJersey[jerseys[playerAssessment._id].color.toLowerCase()] = playersWithJersey[jerseys[playerAssessment._id].color?.toLowerCase()] || [];
          playersWithJersey[jerseys[playerAssessment._id].color.toLowerCase()].push(playerAssessment);
        }
      });
      _.each(playersByPosition[positionName], playerAssessment => {
        if (retainJerseyAssignments && (!_.isNumber(playerAssessment.jerseyNumber) || !playerAssessment.jerseyColour)) {
          playersWithoutJersey.push(playerAssessment);
        } else if (!retainJerseyAssignments && (!jerseys?.[playerAssessment._id] || !_.includes(checkedInPlayerIds || [], playerAssessment._id))) {
          playersWithoutJersey.push(playerAssessment);
        }
      });

      _.each(playersWithJersey, (players, color) => {
        playersWithJersey[color] = _.sortBy(players, playerAssessment => {
          return retainJerseyAssignments ? playerAssessment.jerseyNumber : jerseys?.[playerAssessment._id].number;
        });
      });

      playersWithoutJersey = _.sortBy(playersWithoutJersey, playerAssessment => {
        return retainJerseyAssignments ? playerAssessment.jerseyColour?.toLowerCase() : playerAssessment.player.lastName;
      })

      playersByPosition[positionName] = [..._.flatten(_.values(playersWithJersey)), ...playersWithoutJersey];
    } else {
      playersByPosition[positionName] = _.sortBy(playersByPosition[positionName], playerAssessment => {
        return playerAssessment.player.lastName;
      });
    }
  });

  return playersByPosition;
}

interface IJerseyIcon {
  color?: string;
  url?: string;
}

export const JerseyIcon = styled.div<IJerseyIcon>`
  background-image: url(${props => props.url || jersey});
  background-size: cover;
  background-color: ${props => getJerseyFillColor(props.color)};
  width: 60px;
  height: 60px;
  padding: 22px 0;
  color: ${props => getJerseyTextColor(props.color)};
  text-align: center;
  font-weight: 700;
  font-size: 16px;
  flex-shrink: 0;
`;

interface IColorPaletteItem {
  color: string;
}

interface IColorPalette {
  active?: boolean;
  color: string;
}

const ColorPaletteWrapper = styled.div<IColorPalette>`
  cursor: pointer;
  height: 57px;
  float: left;
  width: 57px;
  border-radius: 4px;
  margin-right: 8px;
  margin-bottom: 8px;
  position: relative;
  background: ${props => getJerseyFillColor(props.color)};
  box-shadow: ${props => props.active ? 'inset 0 0 0 4px rgba(0,0,0,.6)' : 'inset 0 0 0 4px rgba(0,0,0,.2)'};

  & .checkbox {
    background: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAAZCAYAAADE6YVjAAABUElEQVRIid2WzUrDQBSF+2qtIupDuCgWBXXvI4gKig/gQtNa3YjYpmblTktdSYs/NeoDaFUkZTqTTI6LGppJ0vyYiaAXzuYOzHfvPRdmcoVyH27lFQPeXFJ578j9H0heMeBWWoAb5MjXSRaSBpmp9jFdzQiyrBFsNCl2rxn22wxzp0QupFgjYBz4pDaICWhPpvxOOi8cTjx/cPnjOr434Y4VzT+mVJD1SyoAdq7oz7Zr9jA4X1IJLHsEaDwG+zAWUqwRrJ4PsNWi2GszrF34K7x5Hfmgv3NMHUR3LkDm6wQWBwxmY2ANL1J1E5OV4flJV/Rh6Wy8D6HjKqlEqBYAHt44lA4TctutcB9ieeKt2h11PdqH2Nu12aSwbRHQ7XFMVJJtY+QKL6gEdz0O/g1bbMTzIRHE0dGtGbhtUiFp9DuQLF7FQjnkZZQFCv1I/GnIFzUZPB04niNIAAAAAElFTkSuQmCC) no-repeat;
    border-top: 4px solid rgba(0,0,0,.6);
    border-radius: 0 4px 0 0;
    position: absolute;
    bottom: 4px;
    left: 4px;
    width: 29px;
    height: 29px;
    border-right: 4px solid rgba(0,0,0,.6);
    display: ${props => props.active ? 'block' : 'none'};
  }
`;

interface IColorPaletteItem {
  onClick: ClickHandler;
  color: string;
  active?: boolean;
}

export const ColorPaletteItem: React.FC<IColorPaletteItem> = ({ onClick, color, active }) => {
  return <ColorPaletteWrapper
    color={color}
    active={active}
    onClick={onClick}
  >
    <div className="checkbox" />
  </ColorPaletteWrapper>
};

interface IValidateJesery {
  jerseys: { [key: string]: IJersey };
  checkedInPlayersHash: { [key: string]: IPlayerAssessment };
  color?: string,
  number?: number;
  playerAssessment: IPlayerAssessment;
  colorInUse: boolean;
  duplicateConfirmed: boolean;
  playerAssessments: IPlayerAssessment[];
  retainJerseyAssignments: boolean;
}

export const validateJersey = ({ jerseys, checkedInPlayersHash, playerAssessment, color, number, colorInUse, duplicateConfirmed, playerAssessments, retainJerseyAssignments }: IValidateJesery) => {
  let status;

  if (_.isNull(number) || _.isUndefined(number) || _.isNull(color) || _.isUndefined(color)) {
    status = 'WAITING';
  } else if (_.isNumber(number) && number >= 0 && _.isString(color) && color !== '') {
    status = 'VALID';

    const playerAssessmentIds = retainJerseyAssignments ? _.map(playerAssessments, '_id') : Object.keys(jerseys);
    const playerAssessmentsById = _.keyBy(playerAssessments, '_id');

    for (let i = 0; i < playerAssessmentIds.length && status === 'VALID'; i += 1) {
      const currPlayerAssessmentId = playerAssessmentIds[i];
      const currPlayer = playerAssessmentsById[currPlayerAssessmentId];
      const currJersey = retainJerseyAssignments
        ? { color: currPlayer.jerseyColour, number: currPlayer.jerseyNumber }
        : jerseys[currPlayerAssessmentId];

      if (number === currJersey.number
        && color === currJersey.color
        && currPlayer
        && currPlayer?._id !== playerAssessment._id) {
        status = `Jersey is in use by ${getPlayerName(currPlayer)}`;
      }
    }
  } else {
    status = 'Select a color and entered a single number for jersey';
  }

  if (colorInUse && !duplicateConfirmed) {
    status = 'WAITING';
  }

  return status;
}

interface IValidateColor {
  color: string;
  teams: ITeam[];
  playerAssessment?: IPlayerAssessment;
  teamIndex: number;
}

export const validateColor = ({ color, teams, playerAssessment, teamIndex }: IValidateColor) => {
  if (!playerAssessment) return null;

  const activePosition = playerAssessment?.position.name;
  let result = null;

  _.each(teams, (team, currTeamIndex) => {
    _.each(team.colors, (positionColors, positionName) => {
      if ((positionName !== activePosition || Number(teamIndex) !== currTeamIndex) && _.includes(positionColors, color)) {
        result = {
          team: teamIndex + 1,
          position: positionName
        };
      }
    });
  });

  const currTeamColors = _.get(teams, [teamIndex, 'colors', activePosition], []);
  if (_.includes(currTeamColors, color)) {
    result = null;
  }

  return result;
}

// Debug helper function to get player ranking information
export const getPlayerRankingsInfo = (playerAssessment: IPlayerAssessment, allPlayerAssessments: IPlayerAssessment[]) => {
  // Get all players for the same position
  const samePositionPlayers = allPlayerAssessments.filter(p => p.position._id === playerAssessment.position._id);

  // Sort by adjusted score (same logic as getPlayersByPositionAndRank) - create copy to avoid mutating
  const sortedPositionPlayers = [...samePositionPlayers].sort((a, b) => {
    return (b.adjustedScore && b.adjustedScore !== "-" ? Number(b.adjustedScore) : 0) -
           (a.adjustedScore && a.adjustedScore !== "-" ? Number(a.adjustedScore) : 0);
  });

  // Sort all players by adjusted score for overall rank - create copy to avoid mutating
  const sortedAllPlayers = [...allPlayerAssessments].sort((a, b) => {
    return (b.adjustedScore && b.adjustedScore !== "-" ? Number(b.adjustedScore) : 0) -
           (a.adjustedScore && a.adjustedScore !== "-" ? Number(a.adjustedScore) : 0);
  });

  const positionRank = sortedPositionPlayers.findIndex(p => p._id === playerAssessment._id) + 1;
  const overallRank = sortedAllPlayers.findIndex(p => p._id === playerAssessment._id) + 1;
  const adjustedScore = playerAssessment.adjustedScore && playerAssessment.adjustedScore !== "-" ? Number(playerAssessment.adjustedScore) : 0;

  return {
    positionRank,
    overallRank,
    adjustedScore,
    totalPositionPlayers: samePositionPlayers.length,
    totalPlayers: allPlayerAssessments.length
  };
}

// Debug helper function to get team balance information
export const getTeamBalanceInfo = (team: ITeam, allPlayerAssessments: IPlayerAssessment[]) => {
  // Get all players in this team
  const teamPlayers = _.flatten(_.values(team.playersByPosition));

  if (teamPlayers.length === 0) {
    return {
      playerCount: 0,
      averageOverallRank: 0,
      totalScore: 0
    };
  }

  // Sort all players by adjusted score for overall ranking
  const sortedAllPlayers = [...allPlayerAssessments].sort((a, b) => {
    return (b.adjustedScore && b.adjustedScore !== "-" ? Number(b.adjustedScore) : 0) -
           (a.adjustedScore && a.adjustedScore !== "-" ? Number(a.adjustedScore) : 0);
  });

  // Calculate overall ranks and total score for team players
  let totalRank = 0;
  let totalScore = 0;

  teamPlayers.forEach(player => {
    const overallRank = sortedAllPlayers.findIndex(p => p._id === player._id) + 1;
    totalRank += overallRank;

    const playerScore = player.adjustedScore && player.adjustedScore !== "-" ? Number(player.adjustedScore) : 0;
    totalScore += playerScore;
  });

  const averageOverallRank = totalRank / teamPlayers.length;

  return {
    playerCount: teamPlayers.length,
    averageOverallRank: roundTo(averageOverallRank, 1),
    totalScore: roundTo(totalScore, 1)
  };
}
